import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { toast } from "sonner";
import { Flame } from "lucide-react";

export default function AuthDialog({ open, onOpenChange }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back");
      } else {
        await register(form);
        toast.success("Membership created — check your inbox!");
      }
      onOpenChange(false);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-none border-2 border-foreground" data-testid="auth-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 bg-primary flex items-center justify-center">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <DialogTitle className="font-display text-2xl tracking-tighter">
              {mode === "login" ? "Member Sign In" : "Join Fire"}
            </DialogTitle>
          </div>
          <p className="label-tech text-muted-foreground">Fire Sprinkler Network</p>
        </DialogHeader>
        <DialogDescription className="sr-only">Sign in or create a Fire Sprinkler Network membership account.</DialogDescription>

        <Button
          type="button"
          onClick={googleLogin}
          variant="outline"
          className="rounded-none border-foreground w-full font-semibold"
          data-testid="google-login-btn"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" />
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-1">
          <div className="h-px bg-border flex-1" />
          <span className="label-tech text-muted-foreground">or</span>
          <div className="h-px bg-border flex-1" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div>
              <Label className="label-tech">Full Name</Label>
              <Input required value={form.name} onChange={upd("name")} className="rounded-none mt-1" data-testid="auth-name-input" />
            </div>
          )}
          <div>
            <Label className="label-tech">Email</Label>
            <Input type="email" required value={form.email} onChange={upd("email")} className="rounded-none mt-1" data-testid="auth-email-input" />
          </div>
          <div>
            <Label className="label-tech">Password</Label>
            <Input type="password" required value={form.password} onChange={upd("password")} className="rounded-none mt-1" data-testid="auth-password-input" />
          </div>
          {mode === "register" && (
            <div>
              <Label className="label-tech">Company (optional)</Label>
              <Input value={form.company} onChange={upd("company")} className="rounded-none mt-1" data-testid="auth-company-input" />
            </div>
          )}
          {error && <p className="text-sm text-primary font-medium" data-testid="auth-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full rounded-none bg-primary hover:bg-[#D62828] font-bold" data-testid="auth-submit-btn">
            {busy ? "Please wait…" : mode === "login" ? "Sign In" : "Create Membership"}
          </Button>
        </form>

        <p className="text-sm text-center text-muted-foreground">
          {mode === "login" ? "New to the network? " : "Already a member? "}
          <button
            type="button"
            className="text-primary font-bold hover:underline"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            data-testid="auth-toggle-mode"
          >
            {mode === "login" ? "Join now" : "Sign in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
}
